import type { UrlsInText } from '@/@types/post'
import { isElementContentOverflow } from '@/utils/dom'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { css } from '@emotion/react'
import { Link as RouterLink } from '@tanstack/react-router'
import { escapeRegExp, isEmpty, isNil, last, map } from 'lodash-es'
import {
	type HTMLAttributes,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'
import reactStringReplace from 'react-string-replace'
import ClickWrapper from './ClickWrapper'
import Link from './Link'
import MentionUser from './MentionUser'
import MentionUserPlaceholder from './MentionUserPlaceholder'
import UserLoader from './Post/UserLoader'

export interface TextContentProps extends HTMLAttributes<HTMLDivElement> {
	content: string
	urlsInText?: UrlsInText[]

	/**
	 * 是否可折叠
	 *
	 * @default false
	 */
	autoCollapsable?: boolean
}

export default function TextContent({
	content,
	urlsInText = [],
	autoCollapsable = false,
	...props
}: TextContentProps) {
	const [isOverflowInCollapsed, setIsOverflowInCollapsed] = useState<boolean>(false)
	const [isExpanded, setIsExpanded] = useState<boolean>(!autoCollapsable)
	const ref = useRef<HTMLDivElement | null>(null)

	const navigate = useUniversalNavigate()
	const renderContent = useCallback((): ReactNode => {
		const text = content
		if (isEmpty(urlsInText) || isNil(text)) {
			return text
		}

		const reg = new RegExp(
			`(${map(urlsInText, 'originalUrl')
				.map((item) => `${escapeRegExp(item)}`)
				.join('|')})`,
			'g'
		)
		return reactStringReplace(text, reg, (match, index) => {
			const urlInText = urlsInText.find((item) => item.originalUrl === match)
			if (isNil(urlInText)) return null

			switch (urlInText.type) {
				case 'link':
					return (
						<Link
							key={[text, urlInText.originalUrl, index].join('_')}
							href={urlInText.originalUrl}
							onClick={(e) => {
								e.stopPropagation()
								e.preventDefault()
								navigate(urlInText.originalUrl, e)
							}}
						>
							{urlInText.title}
						</Link>
					)
				case 'mention': {
					const username = last(urlInText.url.split('/'))
					if (isNil(username)) {
						return null
					}
					return (
						<RouterLink
							key={[text, username, index].join('_')}
							to="/u/$username"
							params={{
								username,
							}}
							onClick={(e) => {
								e.stopPropagation()
							}}
						>
							<UserLoader
								username={username}
								placeholder={
									<MentionUserPlaceholder
										css={css`
											transform: translateY(3px);
										`}
									/>
								}
							>
								{(user) => (
									<MentionUser
										data={user}
										css={css`
											transform: translateY(3px);
										`}
									/>
								)}
							</UserLoader>
						</RouterLink>
					)
				}
			}
			return null
		})
	}, [content, navigate, urlsInText])

	useEffect(() => {
		const contentElement = ref.current
		if (!contentElement || !autoCollapsable) {
			return
		}

		const isContentOverflow = isElementContentOverflow(contentElement)
		setIsOverflowInCollapsed(isContentOverflow)
		setIsExpanded(!isContentOverflow)
	}, [autoCollapsable])

	if (isEmpty(content)) {
		return null
	}
	return (
		<div {...props}>
			<div
				ref={ref}
				css={css`
					white-space: break-spaces;
					overflow-wrap: anywhere;

					overflow: hidden;
					line-height: 24px;

					${!isExpanded &&
					css`
						max-height: 170px;
					`}

					${autoCollapsable &&
					!isExpanded &&
					css`
						text-overflow: ellipsis;
						display: -webkit-box;
						-webkit-box-orient: vertical;
						-webkit-line-clamp: 7;
					`}
				`}
			>
				{renderContent()}
			</div>

			{autoCollapsable && isOverflowInCollapsed && (
				<ClickWrapper
					whileTap={{
						scale: 0.98,
					}}
					css={css`
						padding-top: 10px;
						color: var(--bg-jike-blue);
						width: fit-content;
					`}
					onClick={(e) => {
						e.stopPropagation()
						setIsExpanded((prev) => !prev)
					}}
				>
					{isExpanded ? '收起' : '查看更多'}
				</ClickWrapper>
			)}
		</div>
	)
}
